<script setup lang="ts">
import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
import 'swiper/css';
import { Swiper, SwiperSlide } from 'swiper/vue';
import type { Swiper as SwiperType } from 'swiper';
import { useI18n } from 'vue-i18n';
import { cabinImages } from '@/utilities/images/cabinImages';
import { cabinTextsCZ, cabinTextsSK } from '@/utilities/texts/cabinTexts';
import { LANGUAGE } from '@/env';

interface Props {
    shipCode: string;
}

const props = defineProps<Props>();

const { t } = useI18n();

const cabinsSwiper = ref<SwiperType | null>(null);
const loadedCabinImages = ref<string[]>([]);
const selectedCabin = ref<string | null>(null);

const instance = getCurrentInstance();
const componentInstanceId = instance!.uid;

// Reactive data sources that depend on the shipCode prop and language
const shipCabinImages = computed(() => cabinImages[props.shipCode] ?? {});
const cabinTexts = computed(() => (LANGUAGE === 'cs' ? cabinTextsCZ : cabinTextsSK));

// A computed list of cabin keys that have both images and a non-empty text description.
const availableCabinKeys = computed(() => {
    const allImageKeys = Object.keys(shipCabinImages.value);
    const allTextKeys = Object.keys(cabinTexts.value);

    return allImageKeys.filter(key => allTextKeys.includes(key) && cabinTexts.value[key]);
});

/**
 * Asynchronously loads the image URLs for a given cabin key.
 */
const loadCabinImages = async (cabinKey: string) => {
    // Reset images while loading
    loadedCabinImages.value = [];

    const imageLoaders = shipCabinImages.value[cabinKey] ?? [];

    if (Array.isArray(imageLoaders)) {
        const imagePromises = imageLoaders.map(loader => loader());
        loadedCabinImages.value = await Promise.all(imagePromises);
    }
};

// --- Swiper Controls ---

const saveCabinsSwiper = (swiperInstance: SwiperType) => {
    cabinsSwiper.value = swiperInstance;
};

const nextSlide = () => {
    cabinsSwiper.value?.slideNext();
};

const prevSlide = () => {
    cabinsSwiper.value?.slidePrev();
};


// --- Lifecycle and Watchers ---

// When the selected cabin changes, load the corresponding images
watch(selectedCabin, (newCabinKey) => {
    if (newCabinKey) {
        loadCabinImages(newCabinKey);
    }
});

// When the component is mounted, set the initially selected cabin to the first available one.
// The watcher will then trigger the initial image load.
onMounted(() => {
    if (availableCabinKeys.value.length > 0) {
        selectedCabin.value = availableCabinKeys.value[0];
    }
});

</script>

<template>
    <section class="section section--light-blue" v-if="availableCabinKeys.length > 0">
        <div class="section__inner">
            <div class="cruiser-page__cabins-container">
                <div class="cruiser-page__cabins-list">
                    <h2 class="cruiser-page__cabins-heading">
                        {{ t('LABELS.cabins') }}
                    </h2>
                    <div class="cruiser-page__cabin-radio-container" v-for="key in availableCabinKeys" :key="key">
                        <input type="radio" :name="`cabin-selection-${componentInstanceId}`"
                            :id="`cabin-selection-${componentInstanceId}-${key}`" :value="key"
                            class="cruiser-page__cabin-radio" v-model="selectedCabin" />
                        <label :for="`cabin-selection-${componentInstanceId}-${key}`"
                            class="cruiser-page__cabin-label">{{ cabinTexts[key] }}</label>
                    </div>
                </div>
                <div class="cruiser-page__cabins-slider-container">
                    <Swiper :slides-per-view="1" class="cabin-showcase__slider cruiser-page__cabin-slider"
                        @swiper="saveCabinsSwiper" :autoplay="{
                            delay: 3000,
                        }">
                        <button class="cabin-showcase__slider-btn cabin-showcase__slider-btn--prev" :class="{
                            'cabin-showcase__slider-btn--disabled':
                                cabinsSwiper?.isBeginning,
                            'cabin-showcase__slider-btn--hidden':
                                cabinsSwiper?.isBeginning &&
                                cabinsSwiper?.isEnd,
                        }" @click="prevSlide">
                            <svg class="cabin-showcase__slider-icon cabin-showcase__slider-icon--left">
                                <use href="@/assets/imgs/icons/sprite.svg#simple-arrow"></use>
                            </svg>
                        </button>
                        <Swiper-slide v-for="(imgUrl, i) in loadedCabinImages" :key="i">
                            <img :src="imgUrl" class="cabin-showcase__slide" :class="{
                                'cabin-showcase__slide--center':
                                    shipCode.startsWith('MSC'),
                            }" />
                        </Swiper-slide>
                        <button class="cabin-showcase__slider-btn cabin-showcase__slider-btn--next" :class="{
                            'cabin-showcase__slider-btn--disabled':
                                cabinsSwiper?.isEnd,
                            'cabin-showcase__slider-btn--hidden':
                                cabinsSwiper?.isEnd &&
                                cabinsSwiper?.isBeginning,
                        }" @click="nextSlide">
                            <svg class="cabin-showcase__slider-icon cabin-showcase__slider-icon--right">
                                <use href="@/assets/imgs/icons/sprite.svg#simple-arrow"></use>
                            </svg>
                        </button>
                    </Swiper>
                </div>
            </div>
        </div>
    </section>
</template>

<style lang="scss" scoped>
@import '@/assets/sass/components/cabin-showcase';
</style>
